import {Avatar} from '@dropbox/dig-components/avatar';
import {LabelGroup} from '@dropbox/dig-components/combinations';
import {List} from '@dropbox/dig-components/list';
import {useQuery} from '@tanstack/react-query';
import {atom, useAtom} from 'jotai';
import React from 'react';
import {getDropboxService, isSandboxMode} from 'utilities';
import {ANALYTICS_EVENTS, sendAnalyticsEvent} from 'utilities/analytics';

import {useGetFeedback} from './feedback';
import {findActiveReviewCycle, getSpriteCycleOfFeedback} from './utils';

type SpritesCycleSelectorProps = {
  dropboxerId: string;
};

export const spritesCycleAtom = atom<string | null>(null);

export const getSpritesYear = (cycle: string) => {
  return cycle.match(/\d{4}/g)?.[0];
};

/**
 * Convert a review cycle name to a normalized SPRiTEs Feedback cycle name.
 * e.g., "2023 Annual SPRiTEs Reviews" -> "2023 SPRiTEs Feedback"
 */
const normalizeReviewCycleToFeedbackCycle = (
  reviewCycle: string
): string | null => {
  const year = getSpritesYear(reviewCycle);
  return year ? `${year} SPRiTEs Feedback` : null;
};

/**
 * Hook to fetch reviews for a dropboxer (legacy cycle fallback when API is empty)
 */
const useGetReviewsForCycles = (dropboxerId: string, enabled: boolean) => {
  const {data} = useQuery({
    queryKey: ['/dropboxer/reviews', dropboxerId],
    queryFn: async () => {
      const response =
        await getDropboxService().getReviewsApiV1DropboxerReviewsEmployeeIdGet(
          dropboxerId
        );
      return response.reviews;
    },
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60, // 1 hour
    enabled,
  });
  return data;
};

/**
 * Fetch review cycles from the API (sandbox or prod table via is_sandbox).
 */
export const useGetReviewCycles = () => {
  const isSandbox = isSandboxMode();
  const {data, isFetched} = useQuery({
    queryKey: ['/review-cycles', isSandbox],
    queryFn: async () => {
      const response =
        await getDropboxService().getReviewCyclesApiV1ReviewCyclesGet(
          isSandbox
        );
      return response.review_cycles;
    },
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
  return {reviewCycles: data, isFetched};
};

/**
 * Legacy prod fallback: derive cycles from feedback + review data when API returns empty.
 */
const useLegacyFeedbackCycles = (dropboxerId: string, enabled: boolean) => {
  const {feedbackState} = useGetFeedback(dropboxerId);
  const reviews = useGetReviewsForCycles(dropboxerId, enabled);

  return React.useMemo(() => {
    if (!enabled) return [];

    const cycles = new Set<string>();

    feedbackState?.allFeedbackIds.forEach((feedbackId) => {
      const {type, date} = feedbackState.byFeedbackId[feedbackId];
      const spriteCycle = getSpriteCycleOfFeedback(type, date);
      if (spriteCycle) {
        cycles.add(spriteCycle);
      }
    });

    reviews?.forEach((review) => {
      const normalizedCycle = normalizeReviewCycleToFeedbackCycle(
        review.review_cycle
      );
      if (normalizedCycle) {
        cycles.add(normalizedCycle);
      }
    });

    return Array.from(cycles).sort().reverse();
  }, [enabled, feedbackState, reviews]);
};

/**
 * Unified cycle hook: API-driven for sandbox and prod, legacy fallback if API empty.
 */
export const useFeedbackCycles = (dropboxerId: string) => {
  const {reviewCycles, isFetched: reviewCyclesFetched} = useGetReviewCycles();
  const legacyCycles = useLegacyFeedbackCycles(
    dropboxerId,
    reviewCyclesFetched && !reviewCycles?.length
  );
  const [selectedSpritesCycle, setSelectedSpritesCycle] =
    useAtom(spritesCycleAtom);

  const sortedSpritesCycles = React.useMemo(() => {
    if (reviewCycles && reviewCycles.length > 0) {
      return reviewCycles.map((c) => c.cycle_name);
    }
    return legacyCycles;
  }, [reviewCycles, legacyCycles]);

  React.useEffect(() => {
    if (sortedSpritesCycles.length === 0) return;

    if (
      selectedSpritesCycle === null ||
      !sortedSpritesCycles.includes(selectedSpritesCycle)
    ) {
      const activeCycle = findActiveReviewCycle(reviewCycles ?? []);
      setSelectedSpritesCycle(
        activeCycle ? activeCycle.cycle_name : sortedSpritesCycles[0]
      );
    }
  }, [
    sortedSpritesCycles,
    selectedSpritesCycle,
    setSelectedSpritesCycle,
    reviewCycles,
  ]);

  const handleSetSpritesCycle = (cycle: string) => {
    if (sortedSpritesCycles.includes(cycle)) {
      setSelectedSpritesCycle(cycle);
    }
  };

  return {sortedSpritesCycles, selectedSpritesCycle, handleSetSpritesCycle};
};

export const SpritesCycleSelector = ({
  dropboxerId,
}: SpritesCycleSelectorProps) => {
  const {sortedSpritesCycles, selectedSpritesCycle, handleSetSpritesCycle} =
    useFeedbackCycles(dropboxerId);

  const handleCycleClick = (cycle: string) => {
    handleSetSpritesCycle(cycle);
    sendAnalyticsEvent(
      ANALYTICS_EVENTS.MANAGER_ASSISTANT.SPRITES_CYCLE_SELECTED,
      {
        category: 'Manager Assistant',
      }
    );
  };

  return (
    <List isSelectable>
      {sortedSpritesCycles.map((cycle) => (
        <List.Item
          key={cycle}
          onClick={() => handleCycleClick(cycle)}
          isSelected={selectedSpritesCycle === cycle}
        >
          <List.Content>
            <LabelGroup
              withText={cycle}
              withLeftAccessory={
                <Avatar size="large">{getSpritesYear(cycle)?.slice(-2)}</Avatar>
              }
            />
          </List.Content>
        </List.Item>
      ))}
    </List>
  );
};
